<template>
    <!--
      Barra de acciones del listado de invitaciones: buscador, selector de
      evento y el botón de generar. Nada más: el endpoint sólo filtra por
      `search`, `evento_busqueda` y `per_page`, así que cualquier otro control
      sería UI muerta.
    -->
    <Card>
        <div v-if="$slots.description" class="tw:mb-4">
            <slot name="description"></slot>
        </div>

        <!--
          Fila completa recién desde `xl` (1280): entre 1024 y 1279 el ancho
          útil (~644 en 1024) no alcanza para los tres controles juntos sin
          aplastar el botón, así que buscador y selector van en su propia fila
          —los dos con el MISMO ancho, `flex-1` cada uno— y el botón ocupa la
          fila de abajo. Desde `xl` los tres vuelven a una sola fila.
        -->
        <div class="tw:flex tw:flex-col tw:xl:flex-row tw:items-stretch tw:xl:items-center tw:gap-4 tw:justify-between">
            <!-- Buscador y selector juntos: son los dos filtros del listado. -->
            <div class="tw:flex tw:flex-col tw:sm:flex-row tw:items-stretch tw:sm:items-center tw:gap-3 tw:w-full tw:xl:max-w-2xl">
                <div class="tw:w-full tw:sm:flex-1">
                    <SearchComponent
                        data-cy="search-invitaciones"
                        placeholder="Buscar"
                        :value="filters.search"
                        :disabled="disabled"
                        @input="onSearch"
                    />
                </div>

                <div class="tw:w-full tw:sm:flex-1">
                    <BaseSelect
                        id="evento_busqueda"
                        data-cy="selector-evento"
                        :value="filters.evento_busqueda"
                        :options="eventoOptions"
                        :disabled="disabled || eventoOptions.length === 0"
                        @input="onEvento"
                    />
                </div>
            </div>

            <!--
              `tw:shrink-0`: en modo fila el botón no debe achicarse. Sin esto,
              cuando la fila queda justa el flex lo comprimía y «Generar
              invitaciones» se partía en dos líneas.
            -->
            <div class="tw:flex tw:justify-end tw:shrink-0">
                <Button
                    variant="default"
                    icon="fas fa-ticket-alt"
                    customClass="tw:w-full tw:xl:w-auto"
                    data-cy="generar-invitaciones"
                    :disabled="disabled || !puedeGenerar"
                    @click="$emit('generar')"
                >
                    Generar invitaciones
                </Button>
            </div>
        </div>
    </Card>
</template>

<script setup>
    import { computed } from 'vue';

    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import BaseSelect from '@/modules/admin/ui/BaseSelect.vue';

    const props = defineProps({
        filters: {
            type: Object,
            required: true
        },
        eventos: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        puedeGenerar: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['update:filters', 'generar']);

    const eventoOptions = computed(() =>
        props.eventos.map(evento => ({ value: evento.id, label: evento.nombre }))
    );

    /**
     * Tanto la búsqueda como el cambio de evento vuelven a la página 1: si el
     * usuario estaba en la 3 y el conjunto filtrado tiene una sola página,
     * quedaría mirando una página vacía.
     */
    function onSearch(search) {
        emit('update:filters', { ...props.filters, search, page: 1 });
    }

    function onEvento(evento_busqueda) {
        emit('update:filters', { ...props.filters, evento_busqueda, page: 1 });
    }
</script>
